// "This period on other models (estimate)" for the AI costs page
// (ANTHROPIC_API_PROVIDER_PLAN Task 8). Pure: it re-prices recorded token totals
// at each allowlisted model's list rates via the shared catalog.

import {
  AI_MODELS,
  ALLOWED_AI_MODEL_IDS,
  repriceUsageUsd,
  type AllowedAiModelId,
  type UsageTotals,
} from "@checklist/shared";
import type { AiCostUsageRow } from "./api.js";

export interface WhatIfRow {
  kind: "model" | "actual";
  /** Catalog model id for "model" rows; empty for the recorded-actual row. */
  modelId: string;
  label: string;
  usd: number;
}

const ZERO: UsageTotals = { inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheCreationTokens: 0 };

/** Sum token totals per recorded model. Cached list copies ("cache") never called a model. */
export function totalsByModel(rows: readonly AiCostUsageRow[]): Map<string, UsageTotals> {
  const map = new Map<string, UsageTotals>();
  for (const r of rows) {
    if (r.modelId === "cache") continue;
    const t = map.get(r.modelId) ?? { ...ZERO };
    t.inputTokens += r.inputTokens;
    t.outputTokens += r.outputTokens;
    t.cacheReadTokens += r.cacheReadTokens;
    t.cacheCreationTokens += r.cacheCreationTokens;
    map.set(r.modelId, t);
  }
  return map;
}

/**
 * One row per allowlisted target model (sum over recorded models of
 * repriceUsageUsd), plus the recorded actual, sorted ascending by cost.
 * Returns [] when the period has no model usage.
 */
export function buildWhatIfRows(rows: readonly AiCostUsageRow[]): WhatIfRow[] {
  const totals = totalsByModel(rows);
  if (totals.size === 0) return [];
  const actual = rows.reduce((sum, r) => (r.modelId === "cache" ? sum : sum + r.estimatedCostUsd), 0);
  const out: WhatIfRow[] = ALLOWED_AI_MODEL_IDS.map((target: AllowedAiModelId) => {
    let usd = 0;
    for (const [from, t] of totals) usd += repriceUsageUsd(t, from, target);
    return { kind: "model" as const, modelId: target, label: AI_MODELS[target].label, usd };
  });
  out.push({ kind: "actual", modelId: "", label: "Recorded (actual)", usd: actual });
  return out.sort((a, b) => a.usd - b.usd);
}
